import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Header } from '@/components/layout/header'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  ArrowLeft,
  Send,
  CheckCircle2,
  Eye,
  XCircle,
  Download,
  Clock,
  ChevronLeft,
  ChevronRight,
  CalendarClock,
  Loader2,
  Ban,
} from 'lucide-react'
import { useWACampaign, useWACampaignMessages, useCancelWACampaign, getWACampaignExportUrl } from '@/hooks/useWACampaigns'
import type { WAMessageStatus } from '@/lib/api-client'

export const Route = createFileRoute('/whatsapp-campaigns_/$id')({
  component: CampaignDetailPage,
})

const statusConfig: Record<WAMessageStatus, { label: string; icon: React.ReactNode; className: string }> = {
  pending: {
    label: 'Pending',
    icon: <Clock className="w-3 h-3" />,
    className: 'bg-muted text-text-muted',
  },
  sent: {
    label: 'Sent',
    icon: <Send className="w-3 h-3" />,
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  delivered: {
    label: 'Delivered',
    icon: <CheckCircle2 className="w-3 h-3" />,
    className: 'bg-success/10 text-success',
  },
  read: {
    label: 'Read',
    icon: <Eye className="w-3 h-3" />,
    className: 'bg-primary/10 text-primary',
  },
  failed: {
    label: 'Failed',
    icon: <XCircle className="w-3 h-3" />,
    className: 'bg-destructive/10 text-destructive',
  },
}

function CampaignDetailPage() {
  const navigate = useNavigate()
  const { id } = Route.useParams()
  const campaignId = Number(id)

  const [page, setPage] = React.useState(1)
  const [statusFilter, setStatusFilter] = React.useState<WAMessageStatus | 'all'>('all')

  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false)
  const cancelMutation = useCancelWACampaign()

  const { data: campaign, isPending: campaignLoading } = useWACampaign(campaignId)
  const { data: messagesData, isPending: messagesLoading } = useWACampaignMessages(
    campaignId,
    { page, limit: 10, status: statusFilter !== 'all' ? statusFilter : undefined }
  )

  const messages = messagesData?.data || []
  const pagination = messagesData?.pagination

  const calcPercent = (count: number, total: number) =>
    total > 0 ? Math.round((count / total) * 100) : 0

  const handleExport = () => {
    window.open(getWACampaignExportUrl(campaignId), '_blank')
  }

  const statusBadge = campaign ? (
    <Badge
      variant="secondary"
      className={cn(
        'text-xs capitalize',
        campaign.status === 'completed' && 'bg-success/10 text-success',
        campaign.status === 'in_progress' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        campaign.status === 'draft' && 'bg-muted text-text-muted',
        campaign.status === 'scheduled' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        campaign.status === 'cancelled' && 'bg-destructive/10 text-destructive',
      )}
    >
      {campaign.status === 'scheduled' ? (
        <span className="flex items-center gap-1">
          <CalendarClock className="w-3 h-3" />
          Scheduled
        </span>
      ) : (
        campaign.status.replace('_', ' ')
      )}
    </Badge>
  ) : null

  const subtitle = campaign
    ? `${campaign.status.replace(`_`, ` `)} \u00b7 ${new Date(campaign.createdAt).toLocaleDateString(`en-MY`, { day: `numeric`, month: `short`, year: `numeric` })}`
    : 'Loading...'

  return (
    <div className="flex flex-col h-full">
      <Header
        title={campaignLoading ? 'Campaign Analytics' : campaign?.name || 'Campaign Analytics'}
        subtitle={subtitle}
      />

      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {/* Top bar: back + export */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 text-text-muted hover:text-text-dark"
            onClick={() => navigate({ to: '/whatsapp-campaigns' })}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Campaigns
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Scheduled Banner */}
        {campaign?.status === 'scheduled' && campaign.scheduledAt && (
          <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/10 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <CalendarClock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Scheduled for{' '}
                  {new Date(campaign.scheduledAt).toLocaleString('en-MY', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                    timeZone: 'Asia/Kuala_Lumpur',
                  })}
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400/80">
                  Campaign will be sent automatically at the scheduled time
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/30 shrink-0"
              onClick={() => setCancelConfirmOpen(true)}
            >
              <Ban className="w-4 h-4 mr-1" />
              Cancel Schedule
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        {campaignLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : campaign ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Sent"
              value={campaign.sentCount}
              total={campaign.totalRecipients}
              percent={calcPercent(campaign.sentCount, campaign.totalRecipients)}
              icon={<Send className="w-4 h-4" />}
              color="blue"
            />
            <StatCard
              label="Delivered"
              value={campaign.deliveredCount}
              total={campaign.sentCount}
              percent={calcPercent(campaign.deliveredCount, campaign.sentCount)}
              icon={<CheckCircle2 className="w-4 h-4" />}
              color="green"
            />
            <StatCard
              label="Read"
              value={campaign.readCount}
              total={campaign.deliveredCount}
              percent={calcPercent(campaign.readCount, campaign.deliveredCount)}
              icon={<Eye className="w-4 h-4" />}
              color="purple"
            />
            <StatCard
              label="Failed"
              value={campaign.failedCount}
              total={campaign.totalRecipients}
              percent={calcPercent(campaign.failedCount, campaign.totalRecipients)}
              icon={<XCircle className="w-4 h-4" />}
              color="red"
            />
          </div>
        ) : null}

        {/* Messages Table */}
        <div className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="text-sm font-medium text-text-dark">
              Recipient List
            </h3>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as WAMessageStatus | 'all')
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-auto">
            {messagesLoading ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-sm text-text-muted">No messages found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Recipient</TableHead>
                    <TableHead className="text-xs">Phone Number</TableHead>
                    <TableHead className="text-xs">Branch</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.map((msg) => {
                    const status = statusConfig[msg.status]
                    const timestamp = msg.readAt || msg.deliveredAt || msg.sentAt || msg.failedAt
                    return (
                      <TableRow key={msg.id}>
                        <TableCell className="py-2">
                          <div className="text-sm font-medium text-text-dark">
                            {msg.patientName || msg.displayName || '-'}
                          </div>
                          {msg.patientPno && (
                            <div className="text-[10px] text-text-muted">
                              {msg.patientPno}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-xs text-text-muted">
                          {msg.waPhoneNumber}
                        </TableCell>
                        <TableCell className="py-2 text-xs text-text-muted">
                          {msg.branchName || '-'}
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge
                            variant="secondary"
                            className={cn('text-[10px] flex items-center gap-1 w-fit', status.className)}
                          >
                            {status.icon}
                            {status.label}
                          </Badge>
                          {msg.failureReason && (
                            <p className="text-[10px] text-destructive mt-0.5 truncate max-w-[150px]" title={msg.failureReason}>
                              {msg.failureReason}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-xs text-text-muted">
                          {timestamp
                            ? new Date(timestamp).toLocaleString('en-US', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-border">
              <p className="text-xs text-text-muted">
                Page {page} / {pagination.totalPages} ({pagination.total} records)
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-7 px-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  className="h-7 px-2"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Schedule Confirmation */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Scheduled Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the schedule and return the campaign to draft status.
              You can reschedule or send it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Schedule</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                cancelMutation.mutate(campaignId, {
                  onSuccess: () => setCancelConfirmOpen(false),
                })
              }}
              disabled={cancelMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Ban className="w-4 h-4 mr-2" />
              )}
              Cancel Schedule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface StatCardProps {
  label: string
  value: number
  total: number
  percent: number
  icon: React.ReactNode
  color: 'blue' | 'green' | 'purple' | 'red'
}

function StatCard({ label, value, total, percent, icon, color }: StatCardProps) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  }

  const barColors = {
    blue: 'bg-blue-500',
    green: 'bg-emerald-500',
    purple: 'bg-purple-500',
    red: 'bg-red-500',
  }

  return (
    <div className="p-3 rounded-xl bg-card border border-border">
      <div className="flex items-center gap-2 mb-2">
        <div className={cn('w-6 h-6 rounded-full flex items-center justify-center', colors[color])}>
          {icon}
        </div>
        <span className="text-xs text-text-muted">{label}</span>
      </div>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="text-xl font-semibold text-text-dark">
          {value.toLocaleString()}
        </span>
        <span className="text-xs text-text-muted">
          / {total.toLocaleString()}
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', barColors[color])}
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="text-[10px] text-text-muted mt-1 text-right">{percent}%</p>
    </div>
  )
}
